/**
 * New / Edit Job Opening — form.  POST /jobs, PATCH /jobs/:id
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button, FloatingInput, FloatingDropdown, Snackbar, Skeleton } from '../../components/ui';
import { Card } from '../../components/ui/Card';
import { Textarea } from '../../components/ui/Input';
import { useJob, useCreateJob, useUpdateJob } from '../../features/recruitment/hooks/useJobs';
import { useDepartmentOptions, usePeopleOptions } from '../../features/recruitment/hooks/useOptions';
import { TagListInput } from '../../features/recruitment/components/TagListInput';
import { InlineError } from '../../features/recruitment';
import { EMPLOYMENT_TYPE_OPTIONS, JOB_STATUS_OPTIONS } from '../../features/recruitment/config/constants';
import { getRecruitmentError } from '../../features/recruitment/utils/apiError';
import { parseDecimal, todayISO } from '../../features/recruitment/utils/format';
import type { EmploymentType, JobStatus, JobOpeningCreate } from '../../services/recruitmentApi';

export const JobOpeningForm: React.FC = () => {
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const isEdit = !!id;
	const jobId = id ? Number(id) : undefined;

	const { data: existing, isLoading: loadingExisting } = useJob(isEdit ? jobId : undefined);
	const { data: departments } = useDepartmentOptions();
	const { data: people } = usePeopleOptions();

	const createJob = useCreateJob();
	const updateJob = useUpdateJob();
	const saving = createJob.isPending || updateJob.isPending;

	// Form state
	const [title, setTitle] = useState('');
	const [departmentId, setDepartmentId] = useState('');
	const [facility, setFacility] = useState('');
	const [employmentType, setEmploymentType] = useState<EmploymentType>('full_time');
	const [openingsCount, setOpeningsCount] = useState('1');
	const [expMin, setExpMin] = useState('');
	const [expMax, setExpMax] = useState('');
	const [required, setRequired] = useState<string[]>([]);
	const [preferred, setPreferred] = useState<string[]>([]);
	const [jdText, setJdText] = useState('');
	const [hiringManagerId, setHiringManagerId] = useState('');
	const [status, setStatus] = useState<JobStatus>('open');
	const [dateOpened, setDateOpened] = useState(todayISO());

	const [touched, setTouched] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; type: 'success' | 'error' }>({
		open: false,
		message: '',
		type: 'success',
	});

	// Populate from existing on edit.
	useEffect(() => {
		if (!isEdit || !existing) return;
		setTitle(existing.title ?? '');
		setDepartmentId(existing.department_id ? String(existing.department_id) : '');
		setFacility(existing.facility_key ?? '');
		setEmploymentType(existing.employment_type ?? 'full_time');
		setOpeningsCount(String(existing.openings_count ?? 1));
		setExpMin(existing.experience_min != null ? String(parseDecimal(existing.experience_min) ?? '') : '');
		setExpMax(existing.experience_max != null ? String(parseDecimal(existing.experience_max) ?? '') : '');
		setRequired(existing.qualifications?.required ?? []);
		setPreferred(existing.qualifications?.preferred ?? []);
		setJdText(existing.jd_text ?? '');
		setHiringManagerId(existing.hiring_manager_id ? String(existing.hiring_manager_id) : '');
		setStatus(existing.status ?? 'open');
		setDateOpened(existing.date_opened ?? todayISO());
	}, [isEdit, existing]);

	const titleValid = title.trim().length > 0;
	const openingsValid = Number(openingsCount) >= 1;
	const expValid =
		!expMin || !expMax || Number(expMin) <= Number(expMax);

	const validate = (): string | null => {
		if (!titleValid) return 'Title is required.';
		if (!openingsValid) return 'Number of openings must be at least 1.';
		if (!expValid) return 'Minimum experience cannot be greater than maximum.';
		return null;
	};

	const handleSubmit = async () => {
		setTouched(true);
		const err = validate();
		if (err) {
			setFormError(err);
			return;
		}
		setFormError(null);

		const qualifications =
			required.length || preferred.length
				? { required: required.length ? required : undefined, preferred: preferred.length ? preferred : undefined }
				: undefined;

		const payload: JobOpeningCreate = {
			title: title.trim(),
			employment_type: employmentType,
			openings_count: Number(openingsCount),
			department_id: departmentId ? Number(departmentId) : null,
			facility_key: facility.trim() || null,
			experience_min: expMin ? Number(expMin) : null,
			experience_max: expMax ? Number(expMax) : null,
			qualifications,
			jd_text: jdText.trim() || null,
			hiring_manager_id: hiringManagerId ? Number(hiringManagerId) : null,
			status,
			date_opened: dateOpened || todayISO(),
		};

		try {
			if (isEdit && jobId) {
				await updateJob.mutateAsync({ id: jobId, data: payload });
				setSnackbar({ open: true, message: 'Job opening updated.', type: 'success' });
				setTimeout(() => navigate(`/recruitment/jobs/${jobId}`), 600);
			} else {
				const created = await createJob.mutateAsync(payload);
				setSnackbar({ open: true, message: 'Job opening created.', type: 'success' });
				setTimeout(() => navigate(`/recruitment/jobs/${created.id}`), 600);
			}
		} catch (e) {
			const re = getRecruitmentError(e);
			setFormError(re.message);
			setSnackbar({ open: true, message: re.message, type: 'error' });
		}
	};

	if (isEdit && loadingExisting) {
		return (
			<div className='min-h-screen bg-gray-50 p-6'>
				<div className='mx-auto max-w-3xl space-y-4'>
					<Skeleton className='h-8 w-48' />
					<Skeleton className='h-96 w-full' />
				</div>
			</div>
		);
	}

	return (
		<div className='min-h-screen bg-gray-50 p-6'>
			<div className='mx-auto max-w-3xl'>
				<button
					type='button'
					onClick={() => navigate(-1)}
					className='mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800'
				>
					<ArrowLeft className='h-4 w-4' /> Back
				</button>

				<h1 className='mb-6 text-2xl font-semibold text-gray-900'>{isEdit ? 'Edit Job Opening' : 'New Job Opening'}</h1>

				<div className='space-y-6'>
					{/* Basics */}
					<Card className='space-y-4'>
						<h2 className='text-sm font-semibold uppercase tracking-wide text-gray-500'>Basics</h2>
						<FloatingInput
							label='Title'
							required
							value={title}
							onChange={setTitle}
							error={touched && !titleValid}
							errorMessage='Title is required.'
						/>
						<div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
							<FloatingDropdown label='Department' options={departments ?? []} value={departmentId} onChange={setDepartmentId} />
							<FloatingInput label='Location / Facility' value={facility} onChange={setFacility} />
						</div>
					</Card>

					{/* Role details */}
					<Card className='space-y-4'>
						<h2 className='text-sm font-semibold uppercase tracking-wide text-gray-500'>Role details</h2>
						<div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
							<FloatingDropdown
								label='Employment type'
								required
								options={EMPLOYMENT_TYPE_OPTIONS}
								value={employmentType}
								onChange={v => setEmploymentType(v as EmploymentType)}
								searchable={false}
							/>
							<FloatingInput
								label='Number of openings'
								required
								type='number'
								value={openingsCount}
								onChange={setOpeningsCount}
								error={touched && !openingsValid}
								errorMessage='At least 1.'
							/>
							<FloatingInput label='Experience min (years)' type='number' value={expMin} onChange={setExpMin} />
							<FloatingInput
								label='Experience max (years)'
								type='number'
								value={expMax}
								onChange={setExpMax}
								error={touched && !expValid}
								errorMessage='Max must be ≥ min.'
							/>
						</div>
						<TagListInput label='Required qualifications' values={required} onChange={setRequired} placeholder='e.g. SSC' />
						<TagListInput label='Preferred qualifications' values={preferred} onChange={setPreferred} placeholder='e.g. Food-safety training' />
					</Card>

					{/* Description */}
					<Card className='space-y-4'>
						<h2 className='text-sm font-semibold uppercase tracking-wide text-gray-500'>Description</h2>
						<Textarea
							label='Job description'
							value={jdText}
							onChange={e => setJdText(e.target.value)}
							rows={5}
							placeholder='Describe the role, responsibilities and what a good candidate looks like…'
						/>
					</Card>

					{/* Ownership */}
					<Card className='space-y-4'>
						<h2 className='text-sm font-semibold uppercase tracking-wide text-gray-500'>Ownership</h2>
						<div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
							<FloatingDropdown label='Hiring manager' options={people ?? []} value={hiringManagerId} onChange={setHiringManagerId} />
							<FloatingDropdown
								label='Status'
								required
								options={JOB_STATUS_OPTIONS}
								value={status}
								onChange={v => setStatus(v as JobStatus)}
								searchable={false}
							/>
							<FloatingInput label='Date opened' required type='date' value={dateOpened} onChange={setDateOpened} />
						</div>
					</Card>

					{formError && <InlineError message={formError} />}

					<div className='flex justify-end gap-2'>
						<Button variant='outline' onClick={() => navigate(-1)} disabled={saving}>
							Cancel
						</Button>
						<Button variant='primary' onClick={handleSubmit} loading={saving}>
							Save Job Opening
						</Button>
					</div>
				</div>
			</div>

			<Snackbar
				open={snackbar.open}
				message={snackbar.message}
				type={snackbar.type}
				onClose={() => setSnackbar(s => ({ ...s, open: false }))}
			/>
		</div>
	);
};
